import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PreferenceMutateOptions,
  PreferenceRequest,
  PreferenceResponse,
  PreferenceResponseStatus,
} from '../../userPreferencesUtils/Types';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';
import { useGetPreferences } from './UseGetPreferences';

export interface UpdatePreferenceMutateResponse<T> {
  data: PreferenceResponse<T> | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  mutate: (variables: PreferenceRequest<T>, options?: PreferenceMutateOptions<T>) => void;
  reset: () => void;
}

/**
 * @return UpdatePreferenceMutateResponse Object
 *
 * @remarks
 * Hooks for update preference api call using useMutate to manage server state
 *
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * **/
export const useUpdatePreferences = <T extends Record<string, any>>(
  type: string,
  namespace: string,
): UpdatePreferenceMutateResponse<T> => {
  const { getAccessToken } = useAuthentication();
  const getPreferences = useGetPreferences(type, namespace);

  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, isError, mutate, reset } = useMutation({
    mutationKey: ['preferences', type, namespace],

    mutationFn: async (request: PreferenceRequest<T>) => {
      const userToken = await getAccessToken();
      const currentPreference = getPreferences.data.find(
        (preference) => preference.namespace === namespace && preference.key === request.key,
      );

      return PreferenceApiService.updatePreferences(
        userToken ?? '',
        currentPreference ? currentPreference.id : '',
        { type, namespace, key: request.key },
        request.data,
      );
    },
    onSuccess: (response) => {
      if (response.status === PreferenceResponseStatus.SUCCESS) {
        queryClient.invalidateQueries({ queryKey: ['preferences'] });
      }
    },
  });

  return {
    data: data ?? null,
    isLoading: isPending,
    isError,
    isSuccess,
    mutate,
    reset,
  };
};
